import { shopVisibilityChanges } from "../utils/shopVisibilityActions";
import React, { useEffect, useState, useMemo } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  setDoc,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDoc,
  onSnapshot,
  orderBy,
  limit as qLimit,
  startAfter,
  runTransaction,
} from "firebase/firestore";
import {
  buildPackageEntry,
  summarizeStorePackages,
} from "../utils/storePackages";

/* ==================== helpers & tiny UI atoms ==================== */
const statusColor = (status) => {
  if (status === "approved") return "bg-green-100 text-green-700";
  if (status === "declined") return "bg-red-100 text-red-700";
  return "bg-yellow-100 text-yellow-800"; // pending/other
};

const Safe = (v, fb = "-") => (v == null || v === "" ? fb : v);

const Badge = ({ tone = "gray", children }) => {
  const tones = {
    gray: "bg-gray-100 text-gray-700",
    blue: "bg-blue-100 text-blue-700",
    green: "bg-green-100 text-green-700",
    red: "bg-red-100 text-red-700",
    yellow: "bg-yellow-100 text-yellow-700",
    purple: "bg-purple-100 text-purple-700",
  };
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
        tones[tone] || tones.gray
      }`}
    >
      {children}
    </span>
  );
};

const Card = ({ title, children, right }) => (
  <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
    {title && (
      <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
        <div className="font-semibold">{title}</div>
        {right}
      </div>
    )}
    <div className="p-4">{children}</div>
  </div>
);

/* ==================== Referral profile resolver ==================== */
/** Resolve a referral user profile from user's referredByUid or referredByReferralCode */
async function fetchReferralProfile(user) {
  if (!user) return null;
  const uid = user?.referredByUid;
  const code = user?.referredByReferralCode;

  try {
    if (uid) {
      const s = await getDoc(doc(db, "referral_users", uid));
      if (s.exists()) return { id: s.id, ...s.data() };
    }
    if (code) {
      const q1 = query(
        collection(db, "referral_users"),
        where("referralCode", "==", code),
      );
      const snap = await getDocs(q1);
      if (!snap.empty) {
        const d = snap.docs[0];
        return { id: d.id, ...d.data() };
      }
    }
  } catch {
    // ignore resolver errors silently
  }
  return null;
}

/* ==================== Lightbox for image preview ==================== */
const ImageLightbox = ({
  open,
  images = [],
  index = 0,
  onClose,
  onPrev,
  onNext,
}) => {
  if (!open || !images.length) return null;
  const item = images[index] || {};
  return (
    <div
      className="fixed inset-0 z-[80]"
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
        if (e.key === "ArrowLeft") onPrev();
        if (e.key === "ArrowRight") onNext();
      }}
      tabIndex={-1}
    >
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="relative max-w-5xl w-full">
          {/* Header */}
          <div className="flex items-center justify-between text-white mb-3">
            <div className="text-sm opacity-80">
              {index + 1} / {images.length} • {item.label || "Preview"}
            </div>
            <button
              className="text-3xl leading-none px-3 py-1 rounded hover:bg-white/10"
              onClick={onClose}
              aria-label="Close preview"
              title="Close (Esc)"
            >
              ×
            </button>
          </div>

          {/* Image */}
          <div className="bg-black/40 rounded-2xl border border-white/10 overflow-hidden">
            <img
              src={item.url}
              alt={item.label || "Preview image"}
              className="max-h-[70vh] w-full object-contain bg-black"
              referrerPolicy="no-referrer"
            />
          </div>

          {/* Controls */}
          {images.length > 1 && (
            <div className="mt-3 flex items-center justify-between">
              <button
                className="px-4 py-2 rounded-xl bg-white/10 text-white hover:bg-white/20"
                onClick={onPrev}
                title="Previous (←)"
              >
                ← Prev
              </button>
              <button
                className="px-4 py-2 rounded-xl bg-white/10 text-white hover:bg-white/20"
                onClick={onNext}
                title="Next (→)"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ==================== Polished Instructor Table ==================== */
const InstructorTable = ({ instructors, onStatusClick, onToggleShop, onDeleteShop, onRestoreShop, shopWorking }) => (
  <div className="mb-10">
    <div className="mb-3 flex items-end justify-between">
      <h3 className="text-xl font-bold text-blue-700">Instructors</h3>
      <Badge tone="blue">{instructors.length} on this page</Badge>
    </div>

    <div className="overflow-x-auto rounded-2xl border bg-white shadow-sm">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 text-gray-600">
            <th className="px-3 py-2 text-left">Name</th>
            <th className="px-3 py-2">Email</th>
            <th className="px-3 py-2">Phone</th>
            <th className="px-3 py-2">Affiliate</th>
            <th className="px-3 py-2">KYC Status</th>
            <th className="px-3 py-2">Shop</th>
            <th className="px-3 py-2">Registered</th>
          </tr>
        </thead>
        <tbody>
          {instructors.map((u, i) => (
            <tr
              key={u.id}
              className={`border-t hover:bg-blue-50/40 transition ${
                i % 2 === 1 ? "bg-gray-50/40" : ""
              }`}
            >
              <td className="px-3 py-2 font-medium">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-indigo-500 to-purple-500 text-white grid place-items-center text-xs">
                    {String(u.fullName || u.email || "U")
                      .slice(0, 2)
                      .toUpperCase()}
                  </div>
                  <div className="truncate" title={u.fullName}>
                    {u.fullName || "-"}
                  </div>
                </div>
              </td>
              <td className="px-3 py-2">
                <span
                  className="truncate inline-block max-w-[220px]"
                  title={u.email}
                >
                  {u.email || "-"}
                </span>
              </td>
              <td className="px-3 py-2">{u.phone || "-"}</td>
              <td className="px-3 py-2">
                {u.referredByUid || u.referredByReferralCode ? (
                  <Badge tone="green">Linked</Badge>
                ) : (
                  <Badge tone="gray">—</Badge>
                )}
              </td>
              <td className="px-3 py-2">
                <button
                  className={`px-2 py-1 rounded ${statusColor(
                    u.status,
                  )} cursor-pointer w-full`}
                  onClick={() => onStatusClick(u)}
                  title="View & update status"
                >
                  {u.status
                    ? u.status.charAt(0).toUpperCase() + u.status.slice(1)
                    : "Pending"}
                </button>
              </td>
              <td className="px-3 py-2">
                {u.shop?.adminDeleted ? (
                  <div className="flex items-center gap-2">
                    <Badge tone="red">Deleted</Badge>
                    <button type="button" disabled={!!shopWorking} onClick={() => onRestoreShop(u)} className="rounded border px-2 py-1 text-blue-700 hover:bg-blue-50 disabled:opacity-50">
                      {shopWorking === u.id ? "Restoring…" : "Restore"}
                    </button>
                  </div>
                ) : u.shop ? (
                  <div className="flex items-center gap-2">
                    <Badge tone={u.shop.adminDisabled ? "red" : "green"}>
                      {u.shop.adminDisabled ? "Disabled" : "Enabled"}
                    </Badge>
                    <button
                      className="px-2 py-1 rounded border hover:bg-gray-50 disabled:opacity-50"
                      disabled={!!shopWorking}
                      onClick={() => onToggleShop(u)}
                    >
                      {shopWorking === u.id ? "Saving…" : u.shop.adminDisabled ? "Enable" : "Disable"}
                    </button>
                    <button
                      className="px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
                      disabled={!!shopWorking}
                      onClick={() => onDeleteShop(u)}
                    >
                      Delete
                    </button>
                  </div>
                ) : <span className="text-gray-500">No shop</span>}
              </td>
              <td className="px-3 py-2 text-gray-700">
                {u.createdAt && u.createdAt.seconds
                  ? new Date(u.createdAt.seconds * 1000).toLocaleString()
                  : "-"}
              </td>
            </tr>
          ))}
          {instructors.length === 0 && (
            <tr>
              <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                No instructors found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

/* ==================== KYC Modal (polished + affiliate + preview) ==================== */
const KycModal = ({ user, onClose, onApprove, onDecline }) => {
  const [showReason, setShowReason] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [refLoading, setRefLoading] = useState(false);
  const [referrer, setReferrer] = useState(null);
  const [refModal, setRefModal] = useState(false);
  const [packages, setPackages] = useState([]);
  const [vipPkgId, setVipPkgId] = useState("");
  const [vipDays, setVipDays] = useState(0);
  const [vipWorking, setVipWorking] = useState(false);
  const [statusWorking, setStatusWorking] = useState(false);

  // lightbox state
  const [lbOpen, setLbOpen] = useState(false);
  const [lbIndex, setLbIndex] = useState(0);
  const images = [
    user?.profilePicUrl
      ? { label: "Profile Pic", url: user.profilePicUrl }
      : null,
    user?.nicFrontUrl ? { label: "NIC Front", url: user.nicFrontUrl } : null,
    user?.nicBackUrl ? { label: "NIC Back", url: user.nicBackUrl } : null,
    user?.bankFrontUrl
      ? { label: "Bank Front Page", url: user.bankFrontUrl }
      : null,
  ].filter(Boolean);

  const openPreviewAt = (idx) => {
    setLbIndex(idx);
    setLbOpen(true);
  };
  const nextImg = () => setLbIndex((i) => (i + 1) % images.length);
  const prevImg = () =>
    setLbIndex((i) => (i - 1 + images.length) % images.length);

  useEffect(() => {
    let active = true;

    const normalizeRows = (snap) =>
      snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((row) => row?.isActive !== false)
        .sort((a, b) => Number(a?.price || 0) - Number(b?.price || 0));

    const loadPackages = async () => {
      try {
        const snap = await getDocs(collection(db, "instructor_packages"));
        if (!active) return;
        setPackages(normalizeRows(snap));
      } catch (error) {
        console.error("[instructor-kyc] initial package fetch failed", error);
        if (active) setPackages([]);
      }
    };

    loadPackages();

    const unsub = onSnapshot(
      collection(db, "instructor_packages"),
      (snap) => {
        if (!active) return;
        setPackages(normalizeRows(snap));
      },
      (error) => {
        console.error("[instructor-kyc] package listener failed", error);
      },
    );

    return () => {
      active = false;
      unsub();
    };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!user) return;
      setRefLoading(true);
      const ref = await fetchReferralProfile(user);
      if (!alive) return;
      setReferrer(ref);
      setRefLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [user]);

  const activateVip = async () => {
    if (!vipPkgId) return alert("Select a package.");
    const pkg = packages.find((p) => p.id === vipPkgId);
    if (!pkg) return alert("Package not found.");

    setVipWorking(true);
    try {
      const duration = Number(vipDays || pkg.durationDays || 30);
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + duration);
      const userRef = doc(db, "users", user.id);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.exists() ? userSnap.data() || {} : {};
      const nextPackage = buildPackageEntry(pkg, {
        expiresAt,
        approvedAt: new Date().toISOString(),
        source: "vip_manual",
      });
      const existingPackages = Array.isArray(userData?.activeStorePackages)
        ? userData.activeStorePackages
        : userData?.storePackage?.packageId
          ? [userData.storePackage]
          : [];
      const activeStorePackages = [...existingPackages, nextPackage];
      const packageState = summarizeStorePackages({
        ...userData,
        activeStorePackages,
      });
      const storePackage = {
        ...nextPackage,
        ...packageState.summary,
        approvedAt: serverTimestamp(),
      };

      await updateDoc(userRef, {
        storePackage,
        activeStorePackages,
        hasStorePackage: true,
      });

      await setDoc(
        doc(db, "stores", user.id),
        {
          instructorId: user.id,
          hasStorePackage: true,
          storePackageStatus: "active",
          storePackageExpiresAt: packageState.summary.expiresAt || expiresAt,
          packageUsageSummary: {
            totalPackages: packageState.summary.totalPackages,
            courseLimit: packageState.summary.courseLimit,
            productLimit: packageState.summary.productLimit,
            serviceLimit: packageState.summary.serviceLimit,
          },
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      await addDoc(collection(db, "users", user.id, "notifications"), {
        type: "storePackage",
        status: "approved",
        message: `Your store package "${pkg.name}" has been activated.`,
        createdAt: serverTimestamp(),
        read: false,
      });

      alert("VIP package activated ✅");
    } catch (e) {
      console.error(e);
      alert("Failed to activate VIP package.");
    } finally {
      setVipWorking(false);
    }
  };

  const handleApproveConfirm = async () => {
    try {
      setStatusWorking(true);
      await onApprove(user);
      setShowApproveConfirm(false);
      setShowReason(false);
    } finally {
      setStatusWorking(false);
    }
  };

  const handleDeclineSubmit = async () => {
    try {
      setStatusWorking(true);
      await onDecline(user, declineReason);
      setShowReason(false);
      setShowApproveConfirm(false);
    } finally {
      setStatusWorking(false);
    }
  };

  if (!user) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl relative overflow-hidden">
        {/* Header */}
        <div className="p-5 bg-gradient-to-r from-indigo-600 via-purple-600 to-fuchsia-600 text-white">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-xl font-bold">Instructor KYC Details</h3>
              <div className="text-xs text-white/90">ID: {user.id}</div>
            </div>
            <button
              className="text-3xl leading-none -mt-2"
              onClick={onClose}
              title="Close"
            >
              ×
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5 max-h-[75vh] overflow-y-auto">
          {/* Basic Info & Affiliate */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Card title="Basic Info">
              <div className="space-y-2 text-sm">
                <div>
                  <strong>Name:</strong> {Safe(user.fullName)}
                </div>
                <div>
                  <strong>Email:</strong> {Safe(user.email)}
                </div>
                <div>
                  <strong>Phone:</strong> {Safe(user.phone)}
                </div>
                <div>
                  <strong>NIC:</strong> {Safe(user.nic)}
                </div>
              </div>
            </Card>

            <Card
              title="Affiliate (Referrer)"
              right={
                <div className="text-xs text-gray-500">
                  {refLoading
                    ? "Resolving…"
                    : referrer
                      ? "Linked"
                      : "Not Linked"}
                </div>
              }
            >
              {referrer ? (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-indigo-500 to-purple-500 text-white grid place-items-center text-xs">
                    {String(referrer.fullName || referrer.email || "A")
                      .slice(0, 2)
                      .toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">
                      {referrer.fullName || referrer.displayName || "Unknown"}
                    </div>
                    <div className="text-xs text-gray-600 truncate">
                      {referrer.email || "-"}
                    </div>
                    <div className="text-xs text-gray-600">
                      {referrer.phone || referrer.phoneNumber || "-"}
                    </div>
                  </div>
                  <button
                    className="px-3 py-1 rounded-lg text-xs bg-indigo-600 text-white hover:bg-indigo-700"
                    onClick={() => setRefModal(true)}
                  >
                    View details
                  </button>
                </div>
              ) : (
                <div className="text-sm text-gray-500">
                  No affiliate profile found.
                </div>
              )}
            </Card>
          </div>

          {/* KYC Images with click-to-preview */}
          <Card title="KYC Images">
            <div className="flex flex-wrap gap-4">
              {images.length ? (
                images.map((img, idx) => (
                  <button
                    key={img.label}
                    className="text-left group"
                    onClick={() => openPreviewAt(idx)}
                    title={`Preview ${img.label}`}
                  >
                    <div className="relative rounded-xl overflow-hidden border">
                      <img
                        src={img.url}
                        alt={img.label}
                        className="w-28 h-28 object-cover group-hover:scale-[1.02] transition"
                        referrerPolicy="no-referrer"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition" />
                    </div>
                    <div className="mt-1 text-xs text-gray-600">
                      {img.label}
                    </div>
                  </button>
                ))
              ) : (
                <div className="text-sm text-gray-500">No images uploaded.</div>
              )}
            </div>
          </Card>

          {/* Bank Details */}
          <Card title="Bank Details">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <strong>Bank Name:</strong> {Safe(user.bankName)}
              </div>
              <div>
                <strong>Account Number:</strong> {Safe(user.bankAccount)}
              </div>
              <div>
                <strong>Branch:</strong> {Safe(user.bankBranch)}
              </div>
              <div>
                <strong>Account Holder:</strong> {Safe(user.accountHolder)}
              </div>
            </div>
          </Card>

          <Card title="VIP — Activate Package (Manual)">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-xs text-gray-500 mb-1">Package</div>
                <select
                  className="w-full border rounded-xl px-3 py-2"
                  value={vipPkgId}
                  onChange={(e) => setVipPkgId(e.target.value)}
                >
                  <option value="">Select package</option>
                  {packages.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} • LKR{" "}
                      {Number(p.price || 0).toLocaleString("en-LK")}
                    </option>
                  ))}
                </select>
                {packages.length === 0 && (
                  <div className="mt-1 text-xs text-amber-600">
                    No active packages loaded. Please verify the packages in
                    `/instructor-packages`.
                  </div>
                )}
              </div>

              <div>
                <div className="text-xs text-gray-500 mb-1">
                  Override days (optional)
                </div>
                <input
                  type="number"
                  className="w-full border rounded-xl px-3 py-2"
                  value={vipDays}
                  onChange={(e) => setVipDays(e.target.value)}
                  placeholder="0 = use package duration"
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={activateVip}
                  disabled={vipWorking || !vipPkgId}
                  className="w-full px-4 py-2 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:bg-gray-200 disabled:text-gray-500"
                >
                  {vipWorking ? "Activating…" : "Activate VIP"}
                </button>
              </div>
            </div>

            <div className="text-xs text-gray-500 mt-2">
              This bypasses the package slip flow and activates the selected
              package immediately. KYC approval is still controlled by the
              main Approve / Decline buttons below.
            </div>
          </Card>

          {/* Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button
              className="w-full py-2 rounded-xl bg-green-600 text-white font-semibold hover:bg-green-700 disabled:bg-gray-200 disabled:text-gray-500"
              onClick={() => setShowApproveConfirm(true)}
              disabled={statusWorking}
            >
              Approve
            </button>
            <button
              className="w-full py-2 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 disabled:bg-gray-200 disabled:text-gray-500"
              onClick={() => setShowReason(true)}
              disabled={statusWorking}
            >
              Decline
            </button>
            <button
              className="w-full py-2 rounded-xl bg-gray-200 text-gray-800 hover:bg-gray-300"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>

          {showReason && (
            <Card title="Reason for Decline">
              <textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                rows={4}
                className="w-full border rounded-xl p-3 bg-gray-50"
                placeholder="Provide a clear reason for declining…"
                required
              />
              <div className="flex gap-2 mt-3">
                <button
                  className={`px-4 py-2 rounded-xl text-white ${
                    declineReason.trim() && !statusWorking
                      ? "bg-red-600 hover:bg-red-700"
                      : "bg-red-300 cursor-not-allowed"
                  }`}
                  onClick={handleDeclineSubmit}
                  disabled={!declineReason.trim() || statusWorking}
                >
                  {statusWorking ? "Submitting..." : "Submit"}
                </button>
                <button
                  className="px-4 py-2 rounded-xl bg-gray-200 text-gray-800 hover:bg-gray-300"
                  onClick={() => setShowReason(false)}
                  disabled={statusWorking}
                >
                  Cancel
                </button>
              </div>
            </Card>
          )}

          {showApproveConfirm && (
            <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4">
              <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border p-6">
                <h4 className="text-xl font-bold text-gray-900">
                  Approve this instructor?
                </h4>
                <p className="mt-2 text-gray-600">
                  This will approve the instructor KYC and unlock the account
                  immediately.
                </p>
                <div className="mt-4 rounded-xl border bg-gray-50 p-4">
                  <div className="font-semibold text-gray-900">
                    {user.fullName || "Unnamed instructor"}
                  </div>
                  <div className="text-sm text-gray-600 break-all">
                    {user.email || "No email"}
                  </div>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    className="px-4 py-2 rounded-xl bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:opacity-60"
                    onClick={() => setShowApproveConfirm(false)}
                    disabled={statusWorking}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 rounded-xl bg-green-600 text-white font-semibold hover:bg-green-700 disabled:opacity-60"
                    onClick={handleApproveConfirm}
                    disabled={statusWorking}
                  >
                    {statusWorking ? "Approving..." : "OK"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Affiliate details full modal */}
        <ReferralDetailsModal
          open={refModal}
          onClose={() => setRefModal(false)}
          referrer={referrer}
        />
      </div>

      {/* Lightbox */}
      <ImageLightbox
        open={lbOpen}
        images={images}
        index={lbIndex}
        onClose={() => setLbOpen(false)}
        onPrev={prevImg}
        onNext={nextImg}
      />
    </div>
  );
};

/* ==================== Filter Bar (new) ==================== */
const FilterBar = ({
  search,
  setSearch,
  status,
  setStatus,
  affiliate,
  setAffiliate,
  pageSize,
  setPageSize,
  onApply,
}) => {
  return (
    <div className="mb-5 bg-white border rounded-2xl shadow-sm p-4">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="md:col-span-2">
          <label className="text-xs text-gray-600">Search</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, email, or phone…"
            className="w-full mt-1 px-3 py-2 rounded-xl border bg-gray-50"
          />
        </div>
        <div>
          <label className="text-xs text-gray-600">Status</label>
          <select
            className="w-full mt-1 px-3 py-2 rounded-xl border bg-gray-50"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="all">All</option>
            <option value="approved">Approved</option>
            <option value="declined">Declined</option>
            <option value="pending">Pending/Other</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-600">Affiliate Link</label>
          <select
            className="w-full mt-1 px-3 py-2 rounded-xl border bg-gray-50"
            value={affiliate}
            onChange={(e) => setAffiliate(e.target.value)}
          >
            <option value="all">All</option>
            <option value="linked">Linked</option>
            <option value="unlinked">Unlinked</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-600">Page size</label>
          <select
            className="w-full mt-1 px-3 py-2 rounded-xl border bg-gray-50"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={30}>30</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          className="px-4 py-2 rounded-xl border hover:bg-gray-50"
          onClick={() => {
            setSearch("");
            setStatus("all");
            setAffiliate("all");
            onApply("reset");
          }}
        >
          Reset
        </button>
        <button
          className="px-4 py-2 rounded-xl bg-gray-900 text-white hover:opacity-90"
          onClick={() => onApply("apply")}
        >
          Apply
        </button>
      </div>
    </div>
  );
};

/* ==================== Page (functions retained; now with filters + pagination) ==================== */
const InstructorList = () => {
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [shopWorking, setShopWorking] = useState(null);
  const [shopError, setShopError] = useState("");

  // Filters
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [affiliate, setAffiliate] = useState("all");

  // Pagination
  const [pageSize, setPageSize] = useState(20);
  const [pageIndex, setPageIndex] = useState(0); // 0-based
  const [pageHasNext, setPageHasNext] = useState(false);
  const [cursorMap, setCursorMap] = useState({}); // page -> lastDoc

  const applyClientFilters = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (arr) =>
      arr.filter((u) => {
        // affiliate filter (client-side)
        const isLinked = !!(u?.referredByUid || u?.referredByReferralCode);
        if (affiliate === "linked" && !isLinked) return false;
        if (affiliate === "unlinked" && isLinked) return false;

        // search filter (client-side)
        if (!q) return true;
        const hay = `${u.fullName || ""} ${u.email || ""} ${
          u.phone || ""
        }`.toLowerCase();
        return hay.includes(q);
      });
  }, [search, affiliate]);

  const buildQuery = async (page) => {
    // server-side filters: role + status (when feasible)
    let qBase = query(
      collection(db, "users"),
      where("role", "==", "instructor"),
      orderBy("createdAt", "desc"),
      qLimit(pageSize),
    );

    if (status === "approved") {
      qBase = query(
        collection(db, "users"),
        where("role", "==", "instructor"),
        where("status", "==", "approved"),
        orderBy("createdAt", "desc"),
        qLimit(pageSize),
      );
    } else if (status === "declined") {
      qBase = query(
        collection(db, "users"),
        where("role", "==", "instructor"),
        where("status", "==", "declined"),
        orderBy("createdAt", "desc"),
        qLimit(pageSize),
      );
    } else if (status === "pending") {
      // "pending/other" → not strictly equal filter (client-side refine after)
      qBase = query(
        collection(db, "users"),
        where("role", "==", "instructor"),
        orderBy("createdAt", "desc"),
        qLimit(pageSize),
      );
    }

    // startAfter cursor for page>0
    if (page > 0 && cursorMap[page]) {
      qBase = query(qBase, startAfter(cursorMap[page]));
    } else if (page > 0 && cursorMap[page - 1]) {
      // fallback: start after previous page's last doc
      qBase = query(qBase, startAfter(cursorMap[page - 1]));
    }

    return qBase;
  };

  const fetchPage = async (page) => {
    setLoading(true);
    const qRef = await buildQuery(page);
    const snap = await getDocs(qRef);
    let items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    // if "pending": keep only not approved/declined
    if (status === "pending") {
      items = items.filter(
        (u) => u.status !== "approved" && u.status !== "declined",
      );
    }

    // client-side search + affiliate filter
    items = applyClientFilters(items);

    items = await Promise.all(items.map(async (user) => {
      const direct = await getDoc(doc(db, "stores", user.id));
      let store = direct;
      if (!direct.exists()) {
        const matches = await getDocs(query(
          collection(db, "stores"), where("instructorId", "==", user.id), qLimit(1),
        ));
        store = matches.docs[0];
      }
      return { ...user, shop: store?.exists() ? { ...store.data(), id: store.id } : null };
    }));
    setInstructors(items);
    setPageHasNext(snap.size === pageSize);
    // save last doc for next page cursor
    const last = snap.docs[snap.docs.length - 1] || null;
    setCursorMap((m) => ({ ...m, [page + 1]: last })); // cursor to reach next page
    setLoading(false);
  };

  // Initial + whenever filters/pageSize change -> reset to first page
  useEffect(() => {
    setPageIndex(0);
    setCursorMap({});
    fetchPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, pageSize]);

  // Apply/Reset button in FilterBar
  const handleApplyFilters = (mode) => {
    if (mode === "reset") {
      setSearch("");
      setStatus("all");
      setAffiliate("all");
      setPageIndex(0);
      setCursorMap({});
      fetchPage(0);
    } else {
      setPageIndex(0);
      setCursorMap({});
      fetchPage(0);
    }
  };

  // Manual refresh when search/affiliate changes (client-side filters) — just refetch current page and reapply client filters
  useEffect(() => {
    fetchPage(pageIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, affiliate]);

  const nextPage = () => {
    if (!pageHasNext) return;
    const p = pageIndex + 1;
    setPageIndex(p);
    fetchPage(p);
  };
  const prevPage = () => {
    if (pageIndex === 0) return;
    const p = pageIndex - 1;
    setPageIndex(p);
    fetchPage(p);
  };

  const handleStatusClick = (user) => setSelectedUser(user);
  const handleCloseModal = () => setSelectedUser(null);

  const updateShopVisibility = async (user, action) => {
    if (!user.shop || shopWorking) return;
    setShopWorking(user.id);
    setShopError("");
    try {
      const storeRef = doc(db, "stores", user.shop.id);
      const shop = await runTransaction(db, async (transaction) => {
        const snapshot = await transaction.get(storeRef);
        if (!snapshot.exists()) {
          throw new Error("This shop no longer exists. Refresh the page.");
        }
        const current = snapshot.data();
        const changes = shopVisibilityChanges(current, action);
        transaction.update(storeRef, changes);
        return { ...current, ...changes, id: snapshot.id };
      });
      setInstructors((rows) => rows.map((row) => row.id === user.id
        ? { ...row, shop }
        : row));
    } catch (error) {
      console.error("Unable to update shop visibility", error);
      setShopError(`Unable to ${action} shop. ${error.message || "Please try again."}`);
    } finally {
      setShopWorking(null);
    }
  };

  const handleToggleShop = (user) =>
    updateShopVisibility(user, user.shop.adminDisabled ? "enable" : "disable");

  const handleRestoreShop = (user) => {
    if (window.confirm(`Restore and publish ${user.shop?.displayName || "this shop"}? An active store package is still required for public access.`)) {
      updateShopVisibility(user, "restore");
    }
  };

  const handleDeleteShop = (user) => {
    if (window.confirm("Delete this shop from the user portal? Existing orders and account records will be kept. An admin can restore it later.")) {
      updateShopVisibility(user, "delete");
    }
  };

  // Approve (UNCHANGED)
  const handleApprove = async (user) => {
    await updateDoc(doc(db, "users", user.id), { status: "approved" });
    await addDoc(collection(db, "users", user.id, "notifications"), {
      type: "kyc",
      status: "approved",
      userId: user.id,
      recipientUid: user.id,
      message:
        "Your KYC has been approved! You now have full access to the platform.",
      createdAt: serverTimestamp(),
      read: false,
    });
    fetchPage(pageIndex);
    setSelectedUser(null);
  };

  // Decline (UNCHANGED)
  const handleDecline = async (user, reason) => {
    await updateDoc(doc(db, "users", user.id), {
      status: "declined",
      declineReason: reason || "",
    });
    await addDoc(collection(db, "users", user.id, "notifications"), {
      type: "kyc",
      status: "declined",
      userId: user.id,
      recipientUid: user.id,
      message: `Your KYC has been declined. Reason: ${
        reason || "No reason provided."
      }`,
      createdAt: serverTimestamp(),
      read: false,
    });
    fetchPage(pageIndex);
    setSelectedUser(null);
  };

  return (
    <div className="w-full py-6 px-3 sm:px-4 lg:px-6 xl:px-8 2xl:px-10">
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-blue-700">All Instructors</h2>
        <p className="text-gray-500 text-sm">KYC approval allows listing creation. Shop visibility is managed separately using Enable, Disable, Delete, or Restore.</p>
      </div>

      {/* Filters */}
      <FilterBar
        search={search}
        setSearch={setSearch}
        status={status}
        setStatus={setStatus}
        affiliate={affiliate}
        setAffiliate={setAffiliate}
        pageSize={pageSize}
        setPageSize={setPageSize}
        onApply={handleApplyFilters}
      />

      {shopError && <p role="alert" className="mb-3 text-sm text-red-600">{shopError}</p>}
      {loading ? (
        <div className="text-gray-500">Loading…</div>
      ) : (
        <>
          <InstructorTable
            instructors={instructors}
            onStatusClick={handleStatusClick}
            onToggleShop={handleToggleShop}
            onDeleteShop={handleDeleteShop}
            onRestoreShop={handleRestoreShop}
            shopWorking={shopWorking}
          />

          {/* Pagination */}
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page <b>{pageIndex + 1}</b>
            </div>
            <div className="flex items-center gap-2">
              <button
                className={`px-4 py-2 rounded-xl border ${
                  pageIndex === 0
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-gray-50"
                }`}
                onClick={prevPage}
                disabled={pageIndex === 0}
              >
                ← Prev
              </button>
              <button
                className={`px-4 py-2 rounded-xl border ${
                  !pageHasNext
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-gray-50"
                }`}
                onClick={nextPage}
                disabled={!pageHasNext}
              >
                Next →
              </button>
            </div>
          </div>
        </>
      )}

      <KycModal
        user={selectedUser}
        onClose={handleCloseModal}
        onApprove={handleApprove}
        onDecline={handleDecline}
      />
    </div>
  );
};

export default InstructorList;

/* ==================== Referral Details Modal (after page export for clarity) ==================== */
const Field = ({ label, value }) => (
  <div className="p-3 border rounded-xl">
    <div className="text-xs text-gray-500">{label}</div>
    <div className="font-medium break-all">{Safe(value)}</div>
  </div>
);

const ReferralDetailsModal = ({ open, onClose, referrer }) => {
  if (!open) return null;
  const name =
    referrer?.fullName || referrer?.displayName || referrer?.name || "Unknown";

  const avatar =
    referrer?.imageUrl ||
    referrer?.avatar ||
    `https://api.dicebear.com/7.x/initials/svg?fontFamily=Inter&seed=${encodeURIComponent(
      name,
    )}`;

  const fmtDate = (ts) => {
    try {
      const d = ts?.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleString();
    } catch {
      return "-";
    }
  };

  return (
    <div className="fixed inset-0 z-[70]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute left-1/2 top-1/2 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-5 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white">
          <div className="flex items-center gap-3">
            <img
              src={avatar}
              alt="referrer"
              className="w-12 h-12 rounded-full border object-cover"
              referrerPolicy="no-referrer"
            />
            <div>
              <div className="text-xl font-semibold">Affiliate — {name}</div>
              <div className="text-sm opacity-90">
                Code: {Safe(referrer?.referralCode)}
              </div>
            </div>
            <button
              className="ml-auto text-white/90 text-2xl leading-none"
              onClick={onClose}
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email" value={referrer?.email} />
            <Field
              label="Phone"
              value={referrer?.phone || referrer?.phoneNumber}
            />
            <Field label="WhatsApp" value={referrer?.whatsapp} />
            <Field label="Telegram" value={referrer?.telegram} />
            <Field label="Address" value={referrer?.address} />
            <Field label="NIC" value={referrer?.nic} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Bank Name" value={referrer?.bankName} />
            <Field label="Account Holder" value={referrer?.accountHolder} />
            <Field label="Bank Branch" value={referrer?.bankBranch} />
            <Field label="Bank Account" value={referrer?.bankAccount} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Joined" value={fmtDate(referrer?.createdAt)} />
            <Field label="User ID" value={referrer?.id} />
          </div>
        </div>
      </div>
    </div>
  );
};
